import React, { useEffect, useMemo, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { Button, Heading, Input, LoadingSpinner, Text } from '../../components/ui'
import { estoqueService } from '../../services/estoque'
import { paymentSourcesService } from '../../services/paymentSources'
import type { MarketingExpense, MarketingExpenseInput } from '../../types/marketingDespesas'
import type { EstoqueFornecedor, PaymentMethod } from '../../types/estoque'
import type { PaymentSource } from '../../types/paymentSources'
import NotasPaymentStep from './NotasPaymentStep'
import {
  SELECT_CLASS,
  TEXTAREA_CLASS,
  emptyPaymentGroup,
  formatCurrency,
  paymentGroupsTotal,
  paymentsFromInvoiceRows,
  toPaymentGroupInputs,
  type PaymentDraftGroup,
} from './notasUi'

type Props = {
  open: boolean
  editing: MarketingExpense | null
  saving: boolean
  onClose: () => void
  onSubmit: (input: MarketingExpenseInput, receiptFile: File | null) => Promise<void>
}

const DespesasFormModal: React.FC<Props> = ({ open, editing, saving, onClose, onSubmit }) => {
  const [actionName, setActionName] = useState('')
  const [supplierId, setSupplierId] = useState('')
  const [paymentSourceId, setPaymentSourceId] = useState('')
  const [description, setDescription] = useState('')
  const [amount, setAmount] = useState(0)
  const [paymentGroups, setPaymentGroups] = useState<PaymentDraftGroup[]>([emptyPaymentGroup(0)])
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([])
  const [fornecedores, setFornecedores] = useState<EstoqueFornecedor[]>([])
  const [fontesPagadoras, setFontesPagadoras] = useState<PaymentSource[]>([])
  const [receiptFile, setReceiptFile] = useState<File | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loadingMeta, setLoadingMeta] = useState(false)

  const today = useMemo(() => new Date().toISOString().slice(0, 10), [])
  const allocated = useMemo(() => paymentGroupsTotal(paymentGroups), [paymentGroups])
  const remaining = Math.round((Number(amount || 0) - allocated) * 100) / 100

  useEffect(() => {
    if (!open) return
    let cancelled = false

    const load = async () => {
      setLoadingMeta(true)
      setError(null)
      try {
        const [methods, fornecedoresData, fontesData] = await Promise.all([
          estoqueService.listFormasPagamento(true),
          estoqueService.listFornecedores(false),
          paymentSourcesService.list(true),
        ])
        if (cancelled) return
        setPaymentMethods(methods.filter((method) => method.is_active))
        setFornecedores(fornecedoresData.filter((f) => f.is_active))

        if (editing) {
          setActionName(editing.action_name || '')
          setSupplierId(editing.supplier_id || '')
          setPaymentSourceId(editing.payment_source_id || '')
          setDescription(editing.description || '')
          setAmount(Number(editing.amount || 0))
          const usedFonteIds = new Set(
            editing.payment_source_id ? [editing.payment_source_id] : []
          )
          setFontesPagadoras(
            fontesData.filter((f) => f.is_active || usedFonteIds.has(f.id))
          )
          const groups = paymentsFromInvoiceRows(editing.payments, methods)
          setPaymentGroups(
            groups.length
              ? groups
              : [emptyPaymentGroup(Number(editing.amount || 0), today)]
          )
        } else {
          setActionName('')
          setSupplierId('')
          setPaymentSourceId('')
          setDescription('')
          setAmount(0)
          setFontesPagadoras(fontesData.filter((f) => f.is_active))
          setPaymentGroups([emptyPaymentGroup(0, today)])
        }
        setReceiptFile(null)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Erro ao carregar formulário.')
        }
      } finally {
        if (!cancelled) setLoadingMeta(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [open, editing, today])

  if (!open) return null

  const handleAmountChange = (value: number) => {
    setAmount(value)
    setPaymentGroups((prev) => {
      if (prev.length !== 1) return prev
      const group = prev[0]
      if (!group || group.installments.length !== 1) return prev
      return [
        {
          ...group,
          installments: [
            {
              ...group.installments[0],
              amount: Math.max(0, value),
            },
          ],
        },
      ]
    })
  }

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError(null)

    const action = actionName.trim()
    if (!action) {
      setError('Informe o nome da ação/evento.')
      return
    }
    if (!supplierId.trim()) {
      setError('Selecione o fornecedor.')
      return
    }
    if (!paymentSourceId.trim()) {
      setError('Selecione a fonte pagadora.')
      return
    }
    if (!Number.isFinite(amount) || amount <= 0) {
      setError('Informe um valor maior que zero.')
      return
    }
    if (!editing && !receiptFile) {
      setError('Anexe o recibo da despesa.')
      return
    }
    if (Math.abs(remaining) > 0.01) {
      setError('O total das parcelas deve ser igual ao valor da despesa.')
      return
    }
    for (const group of paymentGroups) {
      if (!group.payment_method_id) {
        setError('Selecione a forma de pagamento em todos os grupos.')
        return
      }
    }

    const input: MarketingExpenseInput = {
      action_name: action,
      supplier_id: supplierId,
      payment_source_id: paymentSourceId,
      description: description.trim() || null,
      amount,
      payments: toPaymentGroupInputs(paymentGroups),
    }

    try {
      await onSubmit(input, receiptFile)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erro ao salvar despesa.')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/40"
        onClick={saving ? undefined : onClose}
      />
      <div className="relative z-10 flex max-h-[92vh] w-full max-w-4xl flex-col overflow-hidden rounded-xl bg-white shadow-xl dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div>
            <Heading as="h3" size="xl" className="text-gray-900 dark:text-white">
              {editing ? 'Editar despesa' : 'Nova despesa'}
            </Heading>
            <Text size="sm" color="muted" className="mt-1">
              Preencha os dados e a forma de pagamento. Ao salvar, a parcela entra no financeiro.
            </Text>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50 dark:hover:bg-gray-700"
            aria-label="Fechar"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col">
          <div className="flex-1 space-y-5 overflow-y-auto px-5 py-5">
            {error && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300">
                {error}
              </div>
            )}

            {loadingMeta ? (
              <div className="flex justify-center py-16">
                <LoadingSpinner />
              </div>
            ) : (
              <>
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Nome da Ação/Evento <span className="text-red-500">*</span>
                    </label>
                    <Input
                      value={actionName}
                      onChange={(e) => setActionName(e.target.value)}
                      placeholder="Ex.: Feira do Empreendedor"
                      disabled={saving}
                      required
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Fornecedor <span className="text-red-500">*</span>
                    </label>
                    <select
                      className={`w-full ${SELECT_CLASS}`}
                      value={supplierId}
                      onChange={(e) => setSupplierId(e.target.value)}
                      disabled={saving}
                      required
                    >
                      <option value="">Selecione o fornecedor</option>
                      {fornecedores.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Fonte pagadora <span className="text-red-500">*</span>
                    </label>
                    <select
                      className={`w-full ${SELECT_CLASS}`}
                      value={paymentSourceId}
                      onChange={(e) => setPaymentSourceId(e.target.value)}
                      disabled={saving}
                      required
                    >
                      <option value="">Selecione a fonte pagadora</option>
                      {fontesPagadoras.map((f) => (
                        <option key={f.id} value={f.id}>
                          {f.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="sm:col-span-2">
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Descrição
                    </label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={3}
                      disabled={saving}
                      className={TEXTAREA_CLASS}
                      placeholder="Detalhes opcionais da despesa"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Valor <span className="text-red-500">*</span>
                    </label>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={amount || ''}
                      onChange={(e) => handleAmountChange(Number(e.target.value || 0))}
                      disabled={saving}
                      required
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Anexo do Recibo {!editing ? <span className="text-red-500">*</span> : null}
                    </label>
                    <input
                      type="file"
                      accept="image/*,application/pdf"
                      disabled={saving}
                      onChange={(e) => setReceiptFile(e.target.files?.[0] || null)}
                      className="block w-full text-sm text-gray-600 file:mr-3 file:rounded-lg file:border-0 file:bg-primary-50 file:px-3 file:py-2 file:text-sm file:font-medium file:text-primary-700 dark:text-gray-300 dark:file:bg-primary-900/30 dark:file:text-primary-300"
                    />
                    {editing?.attachments?.length ? (
                      <Text size="xs" color="muted" className="mt-1">
                        Recibo atual: {editing.attachments[0].file_name}
                        {receiptFile ? ' (será substituído ao salvar)' : ''}
                      </Text>
                    ) : null}
                    {receiptFile ? (
                      <Text size="xs" color="muted" className="mt-1">
                        Selecionado: {receiptFile.name}
                      </Text>
                    ) : null}
                  </div>
                </div>

                <div className="space-y-3 border-t border-gray-200 pt-4 dark:border-gray-700">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <Heading as="h4" size="lg" className="text-gray-900 dark:text-white">
                      Forma de pagamento
                    </Heading>
                    <Text size="sm" color="muted">
                      Total {formatCurrency(amount)} · Alocado {formatCurrency(allocated)}
                      {Math.abs(remaining) > 0.01
                        ? ` · Diferença ${formatCurrency(remaining)}`
                        : ''}
                    </Text>
                  </div>
                  <NotasPaymentStep
                    groups={paymentGroups}
                    methods={paymentMethods}
                    invoiceTotal={Number(amount || 0)}
                    issueDate={today}
                    onChange={setPaymentGroups}
                  />
                </div>
              </>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-4 dark:border-gray-700">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
              Cancelar
            </Button>
            <Button type="submit" variant="solid" disabled={saving || loadingMeta}>
              {saving ? 'Salvando...' : editing ? 'Salvar alterações' : 'Criar despesa'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default DespesasFormModal
